import React, { useState } from "react";
import classes from "./FilterMenu.module.scss";
import CustomImage from "../../CustomImage";
import { eventTypes } from "../../../utils/constants";
import useIsMobile from "../../../hooks/useIsMobile";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { DateRange, Range } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

interface FilterMenuProps {
  studyDestinationData?: any[];
  cities?: string[];
  fetchEvents?: any;
  setEvents?: any;
  setIsLoading?: any;
  onFilterChange?: (params: any) => void;
}

type DropdownKey = "eventType" | "studyDestination" | "countryCity" | "month";

const categories: { key: DropdownKey; label: string }[] = [
  { key: "eventType", label: "Event Type" },
  { key: "studyDestination", label: "Study Destination" },
  { key: "countryCity", label: "City" },
  { key: "month", label: "Month" },
];

const createEmptyRange = (): Range[] => [
  { startDate: undefined, endDate: undefined, key: "selection" },
];

const FilterMenu: React.FC<FilterMenuProps> = ({
  studyDestinationData = [],
  cities = [],
  fetchEvents,
  setEvents,
  setIsLoading,
  onFilterChange,
}) => {
  const isMobile = useIsMobile(970);
  const [openDropdown, setOpenDropdown] = useState<DropdownKey | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [activeCategory, setActiveCategory] =
    useState<DropdownKey>("eventType");
  const [selectedEventType, setSelectedEventType] = useState<any>(null);
  const [selectedDestination, setSelectedDestination] = useState<any>(null);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<Range[]>(createEmptyRange);
  const [mobFilters, setMobFilters] = useState<any>({});

  const { startDate, endDate } = dateRange[0];
  const noDateSelection = !startDate && !endDate;
  const monthLabel =
    startDate && endDate
      ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
      : "Month";

  const toggleDropdown = (key: DropdownKey) => {
    setOpenDropdown((prev) => (prev === key ? null : key));
  };

  const runFetch = async (params: any) => {
    setOpenDropdown(null);
    onFilterChange?.(params);
    setIsLoading?.(true);
    const response = await fetchEvents?.(params);
    setEvents(response ?? {});
    setIsLoading?.(false);
  };

  const buildParams = (overrides: Record<string, any> = {}) => ({
    eventType: selectedEventType?.value,
    studydestination: selectedDestination,
    city: selectedCity,
    startDate,
    endDate,
    ...overrides,
  });

  const handleEventTypeSelect = (type: any) => {
    setSelectedEventType(type);
    runFetch(buildParams({ eventType: type.value }));
  };

  const handleDestinationSelect = (country: any) => {
    setSelectedDestination(country);
    runFetch(buildParams({ studydestination: country }));
  };

  const handleCitySelect = (city: string) => {
    setSelectedCity(city);
    runFetch(buildParams({ city }));
  };

  const handleMonthApply = () => {
    runFetch(buildParams({ startDate, endDate }));
  };

  const handleClose = () => setShowModal(false);
  const handleShow = () => setShowModal(true);

  const clearFilterClickHandler = () => {
    runFetch({});
    setSelectedEventType(null);
    setSelectedDestination(null);
    setSelectedCity(null);
    setDateRange(createEmptyRange());
  };

  // Mobile: stage the selection only — no request until "Apply Filters"
  const stageMobFilter = (patch: any) => {
    setMobFilters((prev: any) => ({ ...prev, ...patch }));
  };

  const handleApplyFilters = () => {
    setShowModal(false);
    runFetch(mobFilters);
  };

  const handleClearMobFilters = () => {
    setShowModal(false);
    setMobFilters({});
    setDateRange(createEmptyRange());
    runFetch({});
  };

  // ---------- Mobile layout: search bar + menu button + filter modal ----------
  if (isMobile) {
    return (
      <div className={classes.mobileFilter}>
        <div className={classes.searchBar}>
          <input
            className={classes.searchInput}
            type="text"
            name="srch-term"
            id="srch-term"
            placeholder="Search Events"
          />
          <div className={classes.searchIcon}>
            <CustomImage
              src="/searchco.svg"
              alt="search"
              width={14}
              height={14}
            />
          </div>
        </div>

        <div className={classes.menuButton} onClick={handleShow}>
          <CustomImage
            src="/new-images/threeline-menu.svg"
            alt="filters menu"
            width={19}
            height={14}
          />
        </div>

        <Modal
          show={showModal}
          onHide={handleClose}
          className={classes.filterModal}
          centered
        >
          <Modal.Body>
            <div className={classes.categoryTabs}>
              {categories.map((cat) => (
                <div
                  key={cat.key}
                  className={`${classes.categoryTab} ${
                    activeCategory === cat.key ? classes.categoryTabActive : ""
                  }`}
                  onClick={() => setActiveCategory(cat.key)}
                >
                  {cat.label}
                </div>
              ))}
            </div>

            {activeCategory === "month" ? (
              <div
                className={`${classes["mobile-month"]} ${
                  noDateSelection ? classes.emptyRange : ""
                }`}
              >
                <DateRange
                  ranges={dateRange}
                  shownDate={startDate ?? new Date()}
                  onChange={(item) => {
                    setDateRange([item.selection]);
                    stageMobFilter({
                      startDate: item.selection.startDate,
                      endDate: item.selection.endDate,
                    });
                  }}
                  moveRangeOnFirstSelection={false}
                  editableDateInputs
                  startDatePlaceholder="Start Date"
                  endDatePlaceholder="End Date"
                />
              </div>
            ) : (
              <div className={classes.optionGrid}>
                {activeCategory === "eventType" &&
                  eventTypes.map((type: any, index: number) => (
                    <div
                      key={index}
                      className={`${classes.option} ${
                        mobFilters.eventType === type.value
                          ? classes.optionActive
                          : ""
                      }`}
                      onClick={() => stageMobFilter({ eventType: type.value })}
                    >
                      {type.label}
                    </div>
                  ))}

                {activeCategory === "studyDestination" &&
                  studyDestinationData.map((country: any) => (
                    <div
                      key={country}
                      className={`${classes.option} ${
                        classes["study-destination-option"]
                      } ${
                        mobFilters.studydestination === country
                          ? classes.optionActive
                          : ""
                      }`}
                      onClick={() =>
                        stageMobFilter({ studydestination: country })
                      }
                    >
                      {country}
                    </div>
                  ))}

                {activeCategory === "countryCity" &&
                  cities.map((city: string, index: number) => (
                    <div
                      key={index}
                      className={`${classes.option} ${
                        mobFilters.city === city ? classes.optionActive : ""
                      }`}
                      onClick={() => stageMobFilter({ city })}
                    >
                      {city}
                    </div>
                  ))}
              </div>
            )}
          </Modal.Body>

          <Modal.Footer className={classes.modalFooter}>
            <Button
              className={classes.clearBtn}
              variant="secondary"
              onClick={handleClearMobFilters}
            >
              Clear Filters
            </Button>
            <Button
              className={classes.applyBtn}
              variant="primary"
              onClick={handleApplyFilters}
            >
              Apply Filters
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  // ---------- Desktop layout: inline dropdown filters ----------
  return (
    <div className={classes.filterMenu}>
      <div className={classes.sortBy}>
        <h6>Sort By:</h6>
      </div>

      {/* Event Type */}
      <div
        className={`${classes.filterItem} ${
          openDropdown === "eventType" ? classes.open : ""
        }`}
      >
        <div
          className={classes.filterLabel}
          onClick={() => toggleDropdown("eventType")}
        >
          <span>
            {selectedEventType ? selectedEventType.label : "Event Type"}
          </span>
          <CustomImage
            src={
              openDropdown === "eventType"
                ? "/uparrowblue.svg"
                : "/downarrowgr.svg"
            }
            width={20}
            height={8}
            alt="dropdown arrow"
          />
        </div>
        <div className={classes.dropdown}>
          <ul>
            {eventTypes.map((type: any, index: number) => (
              <li
                key={index}
                className={
                  selectedEventType?.value === type.value
                    ? classes.selected
                    : ""
                }
                onClick={() => handleEventTypeSelect(type)}
              >
                {type.label}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Study Destinations */}
      <div
        className={`${classes.filterItem} ${
          openDropdown === "studyDestination" ? classes.open : ""
        }`}
      >
        <div
          className={classes.filterLabel}
          onClick={() => toggleDropdown("studyDestination")}
        >
          <span>
            {selectedDestination ? selectedDestination : "Study Destinations"}
          </span>
          <CustomImage
            src={
              openDropdown === "studyDestination"
                ? "/uparrowblue.svg"
                : "/downarrowgr.svg"
            }
            width={20}
            height={8}
            alt="dropdown arrow"
          />
        </div>
        <div
          className={`${classes.dropdown} ${classes["study-destination-dropdown"]}`}
        >
          <ul>
            {studyDestinationData.map((country: any) => (
              <li
                key={country}
                className={
                  selectedDestination === country ? classes.selected : ""
                }
                onClick={() => handleDestinationSelect(country)}
              >
                {country}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Country & City */}
      <div
        className={`${classes.filterItem} ${
          openDropdown === "countryCity" ? classes.open : ""
        }`}
      >
        <div
          className={classes.filterLabel}
          onClick={() => toggleDropdown("countryCity")}
        >
          <span>{selectedCity || "City"}</span>
          <CustomImage
            src={
              openDropdown === "countryCity"
                ? "/uparrowblue.svg"
                : "/downarrowgr.svg"
            }
            width={20}
            height={8}
            alt="dropdown arrow"
          />
        </div>
        <div className={classes.dropdown}>
          <ul>
            {cities.map((city: string, index: number) => (
              <li
                key={index}
                className={selectedCity === city ? classes.selected : ""}
                onClick={() => handleCitySelect(city)}
              >
                {city}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Month */}
      <div
        className={`${classes.filterItem} ${
          openDropdown === "month" ? classes.open : ""
        }`}
      >
        <div
          className={classes.filterLabel}
          onClick={() => toggleDropdown("month")}
        >
          <span>{monthLabel}</span>
          <CustomImage
            src={
              openDropdown === "month" ? "/uparrowblue.svg" : "/downarrowgr.svg"
            }
            width={20}
            height={8}
            alt="dropdown arrow"
          />
        </div>
        <div
          className={`${classes.dropdown} ${classes["month-dropdown"]} ${
            noDateSelection ? classes.emptyRange : ""
          }`}
        >
          <DateRange
            key={openDropdown === "month" ? "month-open" : "month-closed"}
            ranges={dateRange}
            shownDate={startDate ?? new Date()}
            onChange={(item) => setDateRange([item.selection])}
            moveRangeOnFirstSelection={false}
            editableDateInputs
            startDatePlaceholder="Start Date"
            endDatePlaceholder="End Date"
          />
          <div className={classes.monthApply}>
            <Button
              className={classes.applyBtn}
              variant="primary"
              onClick={handleMonthApply}
            >
              Apply
            </Button>
          </div>
        </div>
      </div>

      {/* Clear Filters */}
      <div className={classes.clearFilter} onClick={clearFilterClickHandler}>
        <h6>Clear Filters</h6>
      </div>
    </div>
  );
};

export default FilterMenu;
